import { capitalize } from '@helpers'
import { Image } from '@components/products/products'
import { API_URL } from '@constants'
import axiosReadInstance from '../../request/axiosReadInstance'
import { useEffect, useState } from 'react'
import logoLt from '../../assets/logo-lt.svg'
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/solid'
import { Button } from '@components/button/button'
interface Socials {
    id: string
    name: string
    url: string
    logo: Image
}

export interface IPath {
    id: string
    attributes: {
        url: string
        name: string
        position: number
    }
}

interface PagesResult {
    data: Array<IPath>
}

interface IFooter {
    copyrightText: string
    workplaceLogo: Image
    lovelyText: string
    slogan: string
    recommendationLogo: Image
    socials: Array<Socials>
    buttonText: string
    buttonUrl: string
    legalNotice: string
    accessibilityText: string
}

// CMS page slugs must resolve from the site root, including on detail pages.
const toRootHref = (url?: string) => {
    if (url === undefined || /^(?:[a-z][a-z0-9+.-]*:|[/?#])/i.test(url)) {
        return url
    }
    return `/${url}`
}

export const Footer = () => {
    const [footer, setFooter] = useState<IFooter>()

    const [loading, setLoading] = useState<boolean>(false)
    const [data, setData] = useState<PagesResult>()

    const fetchPagesData = async () => {
        setLoading(true)
        await axiosReadInstance
            .get(`${API_URL}/api/pages`, {
                params: {
                    sort: 'position:asc',
                    fields: ['url', 'name', 'position'],
                },
            })
            .then((response) => {
                const data = response.data
                setData(data)
            })
            .finally(() => {
                setLoading(false)
            })
    }

    useEffect(() => {
        fetchData()
        fetchPagesData()
    }, [])

    const fetchData = async () => {
        await axiosReadInstance
            .get(`${API_URL}/api/footers?populate=deep`)
            .then((response) => {
                setFooter(response.data.data[0].attributes)
            })
    }
    const slogan = footer?.slogan.split('?')
    const year = new Date().getFullYear()
    useEffect(() => {
        fetchData()
    }, [])

    return (
        <footer
            className="lg:text-left text-gray-600"
            role="contentinfo"
            style={{ background: '#F7F7F8' }}
        >
            <div className="lg:text-left text-gray-600">
                <div className="mx-6 py-4 text-left">
                    <div className="md:grid md:grid-col-1 gap-1">
                        <div className="flex flex-col md:flex-row justify-between">
                            <div>
                                <p
                                    className="
                                font-semibold
                                mb-4
                                flex
                                md:items-center
                                md:justify-start
                            "
                                >
                                    <img src={logoLt} alt="" />
                                    <span lang="en" translate="no">
                                        Listen too
                                    </span>
                                </p>
                                {slogan && slogan?.length > 0 && (
                                    <>
                                        <p
                                            style={{ fontSize: 14 }}
                                        >{`${slogan[0]}?`}</p>
                                        <p
                                            style={{ fontSize: 14 }}
                                        >{`${slogan[1]}?`}</p>
                                    </>
                                )}

                                <Button
                                    variant="secondary"
                                    href={toRootHref(footer?.buttonUrl)}
                                    classList="inline-block mt-4 mb-4"
                                >
                                    {footer?.buttonText}
                                </Button>
                            </div>
                            <div className="flex mb-4">
                                <div className="text-left md:text-center">
                                    <nav
                                        role="navigation"
                                        aria-label="Plan du site"
                                    >
                                        <div
                                            style={{
                                                display: 'inline-block',
                                                textAlign: 'left',
                                            }}
                                        >
                                            {loading
                                                ? null
                                                : data?.data.map(
                                                      (path, index) => (
                                                          <Button
                                                              variant="link"
                                                              href={toRootHref(
                                                                  path.attributes.url.toLowerCase()
                                                              )}
                                                              classList="block mb-4"
                                                              key={index}
                                                              title={capitalize(
                                                                  path
                                                                      .attributes
                                                                      .name
                                                              )}
                                                              lang={
                                                                  path
                                                                      .attributes
                                                                      .url ===
                                                                  'newsroom'
                                                                      ? 'en'
                                                                      : undefined
                                                              }
                                                          >
                                                              {capitalize(
                                                                  path
                                                                      .attributes
                                                                      .name
                                                              )}
                                                          </Button>
                                                      )
                                                  )}
                                        </div>
                                    </nav>
                                </div>
                                <div
                                    className="ml-12 md:ml-1"
                                    style={{ textAlign: 'left' }}
                                >
                                    {footer?.socials.map((social, index) => (
                                        <Button
                                            variant="link"
                                            icon={
                                                <ArrowTopRightOnSquareIcon
                                                    aria-hidden={true}
                                                    className="w-4"
                                                />
                                            }
                                            iconPosition="after"
                                            href={social.url}
                                            classList="mb-4"
                                            target="_blank"
                                            rel="noreferrer"
                                            logo={
                                                social.logo.data.attributes.url
                                            }
                                            logoAlt=""
                                            ariaLabel={`${
                                                social.logo.data.attributes
                                                    .alternativeText
                                            } ${capitalize(social.name)}`}
                                            key={index}
                                        >
                                            {capitalize(social.name)}
                                        </Button>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div>
                            <div className="flex flex-col gap-2 text-center md:grid md:grid-cols-3 md:items-center md:gap-4">
                                <p
                                    className="md:text-left"
                                    style={{ fontSize: '12px' }}
                                >
                                    © {year}{' '}
                                    <span lang="en" translate="no">
                                        Listen too
                                    </span>
                                </p>
                                <p
                                    className="md:text-center"
                                    style={{ fontSize: '12px' }}
                                >
                                    {footer?.lovelyText}
                                </p>
                                <div className="flex flex-row flex-wrap items-center justify-center gap-3 md:justify-end">
                                    {footer?.accessibilityText && (
                                        <a
                                            href="/declaration-accessibilite"
                                            className="hover:underline hover:underline-offset-2 text-center md:text-left"
                                            style={{ fontSize: '12px' }}
                                        >
                                            {footer.accessibilityText}
                                        </a>
                                    )}
                                    <a
                                        href="/mentions-legales"
                                        className="hover:underline hover:underline-offset-2 text-center md:text-left"
                                        style={{ fontSize: '12px' }}
                                    >
                                        {footer?.legalNotice}
                                    </a>
                                    <a
                                        href="/privacy-policy"
                                        className="hover:underline hover:underline-offset-2 text-center md:text-left"
                                        style={{ fontSize: '12px' }}
                                    >
                                        {footer?.legalNotice}
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    )
}
